import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Location } from '@angular/common';
import { MatDrawer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import * as fromAuth from '../../../auth/store';
import { MenuFilterService, NavigationService, UserPermissionsService } from '../../../core/services';
import * as fromRoot from '../../../core/store';
import { IMenuItem, IMenuItem2 } from '../../../core/services/navigation.service';
import { DropdownOption } from '../ebo-dropdown/ebo-dropdown.component';
import * as authActions from '../../../auth/store/auth.actions';
import { ResetPasswordComponent } from '../reset-password/reset-password.component';
import { environment } from 'environments/environment';

@Component({
  selector: 'ebo-sidenav',
  templateUrl: './sidenav.template.html',
  styleUrls: ['./sidenav.component.scss']
})
export class SidenavComponent implements OnInit, OnDestroy {
  @Input() menuLeft = false;
  @Input() drawer: MatDrawer;

  environment$: Observable<string>;
  menuItemsSub: Subscription;

  menuItems: IMenuItem[];
  secondMenuItems: IMenuItem2[] = [
    {
      name: 'Settings',
      svgIcon: 'ebo-settings',
      onClick: () => {
        {
          this.drawer.close().finally();
          this.router.navigateByUrl('/settings');
        }
      }
    },
    {
      name: 'Reset password',
      svgIcon: 'reset-password',
      onClick: () => {
        this.drawer.close().finally();
        this.resetOurPassword();
      }
    },
    {
      name: 'Log out',
      svgIcon: 'logout',
      onClick: () => {
        this.drawer.close().finally();
        this.logOut();
      }
    }
  ];
  hasEnvironmentPermission: boolean;
  desktop = false;
  hideVASelector = false;
  isBotCreated$: Observable<boolean>;
  botOptions$: Observable<DropdownOption[]>;
  bots$: Observable<{ id: number; name: string }[]>;
  selectedBotId$: Observable<number>;
  appVersion = environment.appVersion;

  @HostListener('window:resize')
  onResize(): void {
    this.isUpperTablet();
  }

  constructor(
    private navService: NavigationService,
    private userPermissionsService: UserPermissionsService,
    private store: Store<fromRoot.AppState>,
    private location: Location,
    private router: Router,
    private elementRef: ElementRef,
    private menuFilterService: MenuFilterService,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.isUpperTablet();

    if (sessionStorage.getItem('redirectUrl')) {
      sessionStorage.removeItem('redirectUrl');
    }

    this.environment$ = this.store.pipe(select(fromAuth.getEnvironment));
    this.bots$ = this.store.pipe(select(fromAuth.getBots));
    this.isBotCreated$ = this.store.pipe(select(fromAuth.isBotCreated));
    this.selectedBotId$ = this.store.pipe(select(fromAuth.getBotId));

    // Map bots to dropdown options
    this.botOptions$ = this.bots$.pipe(
      map(bots =>
        bots
          ? bots.map(bot => ({
              label: bot.name,
              value: bot.id
            }))
          : []
      )
    );

    this.hasEnvironmentPermission = this.userPermissionsService.hasPermission('environment.switch');

    // Initial load without feature flags — will be refreshed once flags arrive
    this.menuItemsSub = this.menuFilterService.getFilteredMenuItems().subscribe(menuItems => {
      this.menuItems = menuItems;
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // If menuLeft is true and the drawer opened only
    if (this.menuLeft && this.drawer && this.drawer.opened) {
      const clickedInside = this.elementRef.nativeElement.contains(event.target);
      const clickedOnToggleBtn = (event.target as HTMLElement).closest('.drawer-toggle-btn');

      // If the click is outside the sidenav, close the drawer
      if (!clickedInside && !clickedOnToggleBtn) {
        this.drawer.close();
      }
    }
  }


  isActiveRoute(item: IMenuItem): boolean {
    if (!item.state) {
      return false;
    }
    const currentPath = this.location.path();
    // Remove leading slash for comparison
    const normalizedPath = currentPath.startsWith('/') ? currentPath.substring(1) : currentPath;
    const normalizedState = item.state.startsWith('/') ? item.state.substring(1) : item.state;

    // Check if current path matches or starts with the menu item state
    return normalizedPath === normalizedState || normalizedPath.startsWith(normalizedState + '/');
  }

  navigateTo(route: string): void {
    this.router.navigateByUrl(route);
    if (this.drawer && this.drawer.opened) {
      this.drawer.close().finally();
    }
  }

  setBot(botId: number): void {
    this.store.dispatch(authActions.setBotId({ payload: { botId } }));
  }

  logOut(): void {
    this.store.dispatch(authActions.logout());
  }

  resetOurPassword(): void {
    this.dialog.open(ResetPasswordComponent, {
      panelClass: 'ebo-small-dialog',
      disableClose: true
    });
  }

  private isUpperTablet(): void {
    this.desktop = window.innerWidth > 1080;
  }

  ngOnDestroy(): void {
    if (this.menuItemsSub) {
      this.menuItemsSub.unsubscribe();
    }
  }
}
